import { MarketTableType } from "types";
import React, { useContext, useMemo, useState } from "react";
import { checkSymbolSearch } from "@shared/utils/misc";
import { watchlistService } from "app/services/watchlist.service";
import useSearchInput from "utils/hooks/useSearchInput";
import Table from "common/Table/Table";
import { ColumnsType } from "antd/es/table";
import styled, { useTheme } from "styled-components";
import { DISPLAY_CURRENCIES, MOBILE_SEARCH } from "common/constants";
import Title from "common/Typography/Title";
import Flex from "common/Boxes/Flex";
import AntStarFilled from "@ant-design/icons/StarFilled";
import AntStarOutlined from "@ant-design/icons/StarOutlined";
import { useSelector } from "react-redux";
import { getCoinExtraDetailsRoute } from "utils/routes";
import useHistoryWithQuery from "utils/hooks/useHistoryWithQuery";
import MarketsCardsList from "features/watchlist/AllMarkets/MarketsCardsList";
import useMedia from "utils/hooks/useMedia";
import {
  sortByNumberProperty,
  sortByStringProperty,
} from "@shared/utils/general";
import TextWithIcon from "common/Text/TextWithIcon";
import TextWithPrecisionAndIcon from "common/Text/TextWithPrecisionAndIcon";
import { selectDisplayCurrency } from "@shared/slices/common.slice";
import { usePrecisions } from "@shared/utils/hooks/usePrecisions";
import { ICON_TYPES } from "@shared/common/constants";
import LivePriceChange from "common/LiveComponents/LivePriceChange";
import LiveSymbolPrice from "common/LiveComponents/LiveSymbolPrice";
import AddToWatchlistModal from "features/watchlist/AllMarkets/AddToWatchlistModal";
import useWatchlist from "features/watchlist/common/useWatchlist";
import { LanguageNameSpaces, PrecisionType } from "@shared/types";
import { useBottomNavigator } from "utils/hooks/misc";
import { PageWrapperContext } from "common/Home/PageWrapper/PageWrapper.context.tsx";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import { Panel } from "common/NewLook";
import { getLocaleStringWithPrecision } from "@shared/utils/numbers";
import { NewLookPanelTable } from "features/portfolio/NewLook/styles.ts";
import {
  formatCompactAmount,
  NewLookLiveChange,
  NewLookPair,
  NewLookRange,
} from "features/watchlist/AllMarkets/NewLookMarketCells.tsx";
import { newLookType } from "common/NewLook/tokens.ts";
import { NEW_LOOK_ICON_STROKE } from "common/NewLook/layout.ts";
import { Star } from "lucide-react";
import {
  FROM_LAPTOP_COLUMN,
  FROM_TABLET_COLUMN,
} from "common/NewLook/responsiveColumns.ts";

const AllMarketsTable = () => {
  const { t } = useTypedTranslation(LanguageNameSpaces.WATCHLIST);
  const { t: tCommon } = useTypedTranslation(LanguageNameSpaces.COMMON);
  const { historyPush } = useHistoryWithQuery();
  const appTheme = useTheme();
  const displayCurrency = useSelector(selectDisplayCurrency);
  const { getPrecision } = usePrecisions();
  const isMobile = useMedia([`(max-width: ${MOBILE_SEARCH})`], [true], false);
  const { baseShouldDisplay: isBottomNavigator } = useBottomNavigator();
  const { search, useMobileSearch } = useContext(PageWrapperContext);

  const [clickedMarket, setClickedMarket] = useState<string>();

  const { watchlist, getIsSymbolInWatchlist } = useWatchlist();

  let {
    data: allMarketsData,
    isLoading,
    isFetching,
    refetch,
  } = watchlistService.useGetAllMarketsQuery();

  const volumePrecision = getPrecision(
    displayCurrency === DISPLAY_CURRENCIES.USD
      ? PrecisionType.USD_AMOUNT
      : PrecisionType.BTC_AMOUNT
  );
  const lowHighPricePrecision = getPrecision(
    displayCurrency === DISPLAY_CURRENCIES.USD
      ? PrecisionType.USD_PRICE
      : PrecisionType.BTC_PRICE
  );
  const index = displayCurrency === DISPLAY_CURRENCIES.USD ? 0 : 1;

  const [SearchInput, searchValue] = useSearchInput({
    placeholder: tCommon("labels.search_placeholder"),
    style: {
      width: appTheme.isNewLook ? "220px" : isMobile ? "120px" : "150px",
    },
    lowerCaseValue: true,
  });
  const searchFilter = useMobileSearch ? search.value : searchValue;

  const onClose = () => {
    setClickedMarket(undefined);
  };

  const filteredAllMarkets: MarketTableType[] = useMemo(
    () =>
      allMarketsData
        ?.filter((market) => checkSymbolSearch(market.symbol, searchFilter))
        .map((market) => ({
          ...market,
          key: market.symbol,
          isStarred: +getIsSymbolInWatchlist(market.symbol),
          price: Number(market.price),
          change: market.graph_data.change,
        })) || [],
    [allMarketsData, searchFilter, watchlist?.watchlist]
  );

  const currency = displayCurrency === DISPLAY_CURRENCIES.USD ? "$" : "₿";
  const starColumn = (
    render: (market: MarketTableType) => React.ReactNode
  ) => ({
    title: "",
    showSorterTooltip: false,
    width: 40,
    sorter: (a: MarketTableType, b: MarketTableType) =>
      sortByNumberProperty(a, b, "isStarred"),
    render,
  });
  const renderNewLookStar = (market: MarketTableType) => (
    <StarWrapper
      role="button"
      aria-label={t("card.add_to_watchlist")}
      aria-pressed={!!market.isStarred}
      onClick={(event: React.MouseEvent) => {
        event.stopPropagation();
        setClickedMarket(market.symbol);
      }}
    >
      <NewLookStar
        size={16}
        strokeWidth={NEW_LOOK_ICON_STROKE}
        $starred={!!market.isStarred}
      />
    </StarWrapper>
  );

  const newLookColumns: ColumnsType<MarketTableType> = [
    starColumn(renderNewLookStar),
    {
      title: t("table.columns.symbol"),
      dataIndex: "symbol",
      key: "symbol",
      showSorterTooltip: false,
      sorter: (a, b) => sortByStringProperty(a, b, "symbol"),
      render: (symbol) => <NewLookPair symbol={symbol} />,
    },
    {
      title: t("table.columns.price"),
      dataIndex: "price",
      align: "right",
      showSorterTooltip: false,
      sorter: (a, b) => sortByNumberProperty(a, b, "price"),
      render: (price, market) => (
        <NewLookFigure>
          <LiveSymbolPrice
            symbol={market.symbol}
            exchange={market.exchange}
            initialPrice={price}
            hideIcon={true}
            fontSize={14}
            textColor={appTheme.colors.titleColor}
          />
        </NewLookFigure>
      ),
    },
    {
      title: t("table.columns.24h_change"),
      dataIndex: "graph_data",
      align: "right",
      showSorterTooltip: false,
      sorter: (a, b) => sortByNumberProperty(a, b, "graph_data.change"),
      render: (_graphData, market) => (
        <NewLookLiveChange
          symbol={market.symbol}
          exchange={market.exchange}
          initialChange={market.graph_data.change}
        />
      ),
    },
    {
      title: t("table.columns.24h_low_high"),
      showSorterTooltip: false,
      width: 260,
      ...FROM_LAPTOP_COLUMN,
      sorter: {
        compare: (a, b) => (a.high_price?.[0] || 0) - (b.high_price?.[0] || 0),
      },
      render: (market: MarketTableType) => (
        <NewLookRange
          graphData={market.graph_data}
          low={market.low_price?.[index]}
          high={market.high_price?.[index]}
          currency={currency}
          precision={lowHighPricePrecision}
        />
      ),
    },
    {
      title: t("table.columns.24h_volume"),
      dataIndex: "volume",
      align: "right",
      showSorterTooltip: false,
      ...FROM_TABLET_COLUMN,
      sorter: {
        compare: (a, b) => a.volume[0] - b.volume[0],
      },
      render: (volume) =>
        volume[index] !== null && (
          <NewLookFigure
            title={getLocaleStringWithPrecision(volume[index], volumePrecision)}
          >
            {formatCompactAmount(volume[index], currency)}
          </NewLookFigure>
        ),
    },
  ];

  const classicColumns: ColumnsType<MarketTableType> = [
    {
      title: "",
      showSorterTooltip: false,
      width: 40,
      sorter: (a, b) => sortByNumberProperty(a, b, "isStarred"),
      render: (market) => {
        const onClick = (e: React.MouseEvent<HTMLSpanElement, MouseEvent>) => {
          e.stopPropagation();
          setClickedMarket(market.symbol);
        };
        return (
          <StarWrapper onClick={onClick}>
            {appTheme.isNewLook ? (
              <NewLookStar
                size={16}
                strokeWidth={NEW_LOOK_ICON_STROKE}
                $starred={!!market.isStarred}
              />
            ) : market.isStarred ? (
              <StarFilled />
            ) : (
              <StarOutlined />
            )}
          </StarWrapper>
        );
      },
    },
    {
      title: t("table.columns.symbol"),
      dataIndex: "symbol",
      key: "symbol",
      showSorterTooltip: false,
      sorter: (a, b) => sortByStringProperty(a, b, "symbol"),
      render: (symbol) =>
        appTheme.isNewLook ? (
          <TextWithIcon
            text={symbol}
            coinOrExchangeName={symbol}
            pxIconSize={36}
            iconType={ICON_TYPES.COIN}
            textStyle={{
              fontWeight: appTheme.typography.fontWeights.MEDIUM,
            }}
          />
        ) : (
          <TextWithIcon
            text={symbol}
            coinOrExchangeName={symbol}
            iconSize={"l"}
            iconType={ICON_TYPES.COIN}
          />
        ),
    },
    {
      title: t("table.columns.price"),
      dataIndex: "price",
      showSorterTooltip: false,
      sorter: (a, b) => sortByNumberProperty(a, b, "price"),
      render: (price, market) => (
        <LiveSymbolPrice
          symbol={market.symbol}
          exchange={market.exchange}
          initialPrice={price}
          // The pair already names the quote coin; skip its icon per row.
          hideIcon={appTheme.isNewLook}
          style={appTheme.isNewLook ? NEW_LOOK_NUMBER_STYLE : undefined}
          textProps={{
            iconType: ICON_TYPES.COIN,
            coinOrExchangeName: market.symbol?.split("/")[1],
            iconSize: "l",
          }}
        />
      ),
    },
    {
      title: t("table.columns.24h_change"),
      dataIndex: "graph_data",
      showSorterTooltip: false,
      sorter: (a, b) => sortByNumberProperty(a, b, "graph_data.change"),
      render: (_graphData, market) => (
        <div style={{ width: "fit-content" }}>
          <LivePriceChange
            symbol={market.symbol}
            exchange={market.exchange}
            initialChange={market.graph_data.change}
            size={appTheme.typography.pxFontSizes.S}
            useBackground={true}
          />
        </div>
      ),
    },
    {
      title: t("table.columns.24h_low_high"),
      showSorterTooltip: false,
      sorter: {
        compare: (a, b) => (a.high_price?.[0] || 0) - (b.high_price?.[0] || 0),
      },
      render: (market) =>
        market.low_price !== null &&
        market.low_price[index] !== null &&
        market.high_price[index] !== null && (
          <>
            <TextWithPrecisionAndIcon
              text={market.low_price[index]}
              precision={lowHighPricePrecision}
              iconType={ICON_TYPES.DISPLAY_CURRENCY}
              iconSize={"l"}
            />
            <span style={{ padding: "0 4px" }}>/</span>
            <TextWithPrecisionAndIcon
              text={market.high_price[index]}
              precision={lowHighPricePrecision}
              iconType={ICON_TYPES.DISPLAY_CURRENCY}
              iconSize={"l"}
            />
          </>
        ),
    },
    {
      title: t("table.columns.24h_volume"),
      dataIndex: "volume",
      showSorterTooltip: false,
      sorter: {
        compare: (a, b) => a.volume[0] - b.volume[0],
      },
      render: (volume) =>
        volume[index] !== null && (
          <TextWithPrecisionAndIcon
            text={volume[index]}
            precision={volumePrecision}
            iconType={ICON_TYPES.DISPLAY_CURRENCY}
          />
        ),
    },
  ];

  const columns = appTheme.isNewLook ? newLookColumns : classicColumns;

  const marketsTable = useMemo(
    () => (
      <Table<MarketTableType>
        columns={columns}
        dataSource={filteredAllMarkets}
        loading={isLoading}
        rowClassName="u-cursorPointer"
        onRow={(market) => ({
          onClick: () =>
            historyPush(
              getCoinExtraDetailsRoute(location.pathname || "", market.symbol)
            ),
        })}
        style={appTheme.isNewLook ? undefined : { minHeight: 450 }}
      />
    ),
    [filteredAllMarkets, isLoading, appTheme.isNewLook]
  );

  const marketsCardsList = (
    <MarketsCardsList
      data={filteredAllMarkets}
      showExchangesModal={setClickedMarket}
      loading={isLoading}
      refreshing={isFetching}
      onRefresh={() => {
        try {
          refetch();
        } catch {}
      }}
    />
  );

  if (appTheme.isNewLook && !isBottomNavigator) {
    return (
      <>
        <Panel
          title={t("all_markets.title")}
          count={isLoading ? undefined : filteredAllMarkets.length}
          actions={!useMobileSearch && SearchInput}
          flush={true}
        >
          <NewLookPanelTable>{marketsTable}</NewLookPanelTable>
        </Panel>
        <AddToWatchlistModal
          visible={!!clickedMarket}
          symbol={clickedMarket}
          onClose={onClose}
        />
      </>
    );
  }

  return (
    <div style={{ height: "100%" }}>
      {!isBottomNavigator && (
        <Flex style={{ marginBottom: 16 }}>
          <Title type="secondary" level={2}>
            {t("all_markets.title")}
          </Title>
          <Flex>{SearchInput}</Flex>
        </Flex>
      )}
      {isBottomNavigator ? marketsCardsList : marketsTable}
      <AddToWatchlistModal
        visible={!!clickedMarket}
        symbol={clickedMarket}
        onClose={onClose}
      />
    </div>
  );
};

export default AllMarketsTable;

const StarWrapper = styled.div`
  position: absolute;
  width: 100%;
  height: 100%;
  top: 0;
  left: 0;
  display: flex;
  align-items: center;
  justify-content: center;
`;
const StarOutlined = styled(AntStarOutlined)`
  color: ${(p) => p.theme.colors.secondaryGrey};
  cursor: pointer;
  &:hover {
    color: ${(p) => p.theme.colors.primaryBlue};
  }
`;
const StarFilled = styled(AntStarFilled)`
  color: ${(p) => p.theme.colors.primaryBlue};
`;

const NEW_LOOK_NUMBER_STYLE = { fontVariantNumeric: "tabular-nums" as const };

const NewLookFigure = styled.span`
  display: inline-block;
  ${newLookType.figure};
  font-size: 14px;
  white-space: nowrap;
  color: ${(p) => p.theme.colors.titleColor};
  * {
    font-size: inherit !important;
    font-weight: inherit !important;
  }
`;

const NewLookStar = styled(Star)<{ $starred: boolean }>`
  cursor: pointer;
  color: ${(p) =>
    p.$starred ? p.theme.colors.primaryBlue : p.theme.colors.lightestText};
  fill: ${(p) => (p.$starred ? p.theme.colors.primaryBlue : "none")};
  transition: color 150ms, fill 150ms;
  &:hover {
    color: ${(p) => p.theme.colors.primaryBlue};
  }
`;
